import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Avatar,
  InputAdornment,
  Paper,
  ListItemAvatar,
  Card,
  CardContent,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authService, UserAsset } from '../../../services/authService';
import { cryptoIconsService } from '../../../services/cryptoIconsService';
import { cryptoService } from '../../../services/cryptoService';

interface PopularCrypto {
  symbol: string;
  name: string;
  iconUrl?: string;
}

interface SearchResult {
  symbol: string;
  name: string;
  iconUrl?: string;
}

const WelcomeCapitalScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // États pour le formulaire
  const [symbol, setSymbol] = useState('');
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);
  const [currentPrice, setCurrentPrice] = useState<number | null>(null);
  
  // États pour la gestion des actifs
  const [assets, setAssets] = useState<UserAsset[]>([]);
  
  // États pour le chargement et les erreurs
  const [loading, setLoading] = useState(false);
  const [isPriceLoading, setIsPriceLoading] = useState(false);
  const [error, setError] = useState('');
  
  // États pour la recherche et les cryptos populaires
  const [popularCryptos, setPopularCryptos] = useState<PopularCrypto[]>([]);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [allCryptos, setAllCryptos] = useState<{ [key: string]: { name: string, image_path: string } }>({});

  const getCryptoFullName = (symbol: string): string => {
    const names: { [key: string]: string } = {
      'BTC': 'Bitcoin',
      'ETH': 'Ethereum',
      'BNB': 'BNB',
      'SOL': 'Solana',
      'ADA': 'Cardano',
      'DOT': 'Polkadot',
      'MATIC': 'Polygon',
      'AVAX': 'Avalanche',
      'LINK': 'Chainlink',
      'UNI': 'Uniswap',
    };
    return names[symbol] || symbol;
  };

  const loadCryptoIcons = useCallback(async () => {
    try {
      const popularSymbols = ['BTC', 'ETH', 'BNB', 'SOL', 'ADA', 'DOT', 'MATIC', 'AVAX', 'LINK', 'UNI'];
      
      const cryptos = await Promise.all(popularSymbols.map(async (symbol) => {
        const iconUrl = await cryptoIconsService.getCryptoIconUrl(symbol);
        return {
          symbol,
          name: getCryptoFullName(symbol),
          iconUrl: iconUrl || undefined,
        };
      }));
      
      setPopularCryptos(cryptos);
    } catch (err) {
      console.error('Erreur lors du chargement des icônes:', err);
      // Fallback vers les cryptos hardcodées
      setPopularCryptos([
        { symbol: 'BTC', name: 'Bitcoin' },
        { symbol: 'ETH', name: 'Ethereum' },
        { symbol: 'BNB', name: 'BNB' },
        { symbol: 'SOL', name: 'Solana' },
        { symbol: 'ADA', name: 'Cardano' },
        { symbol: 'DOT', name: 'Polkadot' },
        { symbol: 'MATIC', name: 'Polygon' },
        { symbol: 'AVAX', name: 'Avalanche' },
        { symbol: 'LINK', name: 'Chainlink' },
        { symbol: 'UNI', name: 'Uniswap' },
      ]);
    }
  }, []);

  const loadAllCryptos = async () => {
    try {
      const response = await fetch('https://cdn.cryptonvx.com/icons-data/index.json');
      if (response.ok) {
        const data = await response.json() as { [key: string]: { name: string, image_path: string } };
        setAllCryptos(data);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des cryptos:', err);
    }
  };

  useEffect(() => {
    loadCryptoIcons();
    loadAllCryptos();
  }, [loadCryptoIcons]);

  const handleSearch = (searchTerm: string) => {
    setSymbol(searchTerm.toUpperCase());
    if (!searchTerm.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    const searchTermUpper = searchTerm.toUpperCase();
    const results = Object.entries(allCryptos)
      .filter(([symbol, data]) => 
        symbol.includes(searchTermUpper) || 
        data.name.toUpperCase().includes(searchTermUpper)
      )
      .slice(0, 5)
      .map(([symbol, data]) => ({
        symbol,
        name: data.name,
        iconUrl: `https://cdn.cryptonvx.com/icons-data/${data.image_path}`
      }));

    setSearchResults(results);
    setShowSearchResults(true);
  };

  const selectSearchResult = (crypto: SearchResult) => {
    setSymbol(crypto.symbol);
    setSelectedSymbol(crypto.symbol);
    setShowSearchResults(false);
    setError('');
    fetchCurrentPrice(crypto.symbol);
  };

  const fetchCurrentPrice = async (cryptoSymbol: string) => {
    if (!cryptoSymbol) return;
    
    setIsPriceLoading(true);
    setCurrentPrice(null);
    
    try {
      const priceValue = await cryptoService.getCurrentPrice(cryptoSymbol);
      if (priceValue !== null) {
        setCurrentPrice(priceValue);
        setPrice(priceValue.toFixed(2));
      } else {
        throw new Error(t('onb.capital.priceUnavailable'));
      }
    } catch (err) {
      console.error('Erreur lors de la récupération du prix:', err);
      setError(t('onb.capital.fetchPriceError'));
    } finally {
      setIsPriceLoading(false);
    }
  };

  const selectCrypto = (cryptoSymbol: string, cryptoName: string) => {
    setSelectedSymbol(cryptoSymbol);
    setSymbol(cryptoSymbol);
    setError('');
    fetchCurrentPrice(cryptoSymbol);
  };

  const handleAddAsset = () => {
    const finalSymbol = selectedSymbol || symbol.trim().toUpperCase();
    const quantityValue = parseFloat(quantity.replace(',', '.'));
    const priceValue = parseFloat(price.replace(',', '.'));

    if (!finalSymbol) {
      setError(t('addCrypto.symbolRequired'));
      return;
    }
    if (!quantity || quantityValue <= 0) {
      setError(t('addCrypto.quantityPositive'));
      return;
    }
    if (!price || priceValue <= 0) {
      setError(t('addCrypto.pricePositive'));
      return;
    }

    setError('');
    
    const newAsset: UserAsset = {
      symbol: finalSymbol,
      amount: quantityValue,
      purchasePrice: priceValue,
    };

    setAssets([...assets, newAsset]);
    
    // Reset du formulaire
    setSymbol('');
    setQuantity('');
    setPrice('');
    setSelectedSymbol(null);
    setCurrentPrice(null);
  };

  const handleDeleteAsset = (index: number) => {
    setAssets(assets.filter((_, i) => i !== index));
  };

  const handleNext = async () => {
    setLoading(true);
    setError('');
    
    try {
      await authService.saveUserAssets(assets);
      navigate('/onboarding/complete');
    } catch (err: any) {
      setError(err.message || t('onb.capital.saveAssetsError'));
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    const finalSymbol = selectedSymbol || symbol.trim();
    const quantityValue = parseFloat(quantity.replace(',', '.'));
    const priceValue = parseFloat(price.replace(',', '.'));
    
    return finalSymbol && quantityValue > 0 && priceValue > 0;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#000000',
        p: { xs: 3, md: 4 },
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '700px',
          p: { xs: 4, md: 8 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          backgroundColor: '#000000',
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
        }}
      >
        <Typography variant="h4" sx={{ mb: 2, color: '#fff', textAlign: 'center' }}>
          {t('onb.capital.title')}
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: 'rgba(255,255,255,0.7)', textAlign: 'center' }}>
          {t('onb.capital.subtitle')}
        </Typography>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              backgroundColor: 'rgba(244, 67, 54, 0.1)',
              border: '1px solid rgba(244, 67, 54, 0.3)',
              color: '#ff6b6b',
            }}
          >
            {error}
          </Alert>
        )}

        {/* Cryptomonnaies populaires */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              color: '#f1c086',
              fontWeight: 'bold',
              mb: 2,
            }}
          >
            {t('addCrypto.popularCryptos')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {popularCryptos.map((crypto) => {
              const isSelected = selectedSymbol === crypto.symbol;
              return (
                <Chip
                  key={crypto.symbol}
                  avatar={
                    crypto.iconUrl ? (
                      <Avatar
                        src={crypto.iconUrl}
                        sx={{ width: 20, height: 20 }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <Avatar
                        sx={{
                          width: 20,
                          height: 20,
                          backgroundColor: 'rgba(241, 192, 134, 0.3)',
                          color: '#f1c086',
                          fontSize: '10px',
                        }}
                      >
                        {crypto.symbol[0]}
                      </Avatar>
                    )
                  }
                  label={`${crypto.symbol} - ${crypto.name}`}
                  onClick={() => selectCrypto(crypto.symbol, crypto.name)}
                  sx={{
                    backgroundColor: isSelected ? '#f1c086' : '#1a1a1a',
                    color: isSelected ? '#000' : '#fff',
                    border: `1px solid ${isSelected ? '#f1c086' : '#333'}`,
                    '&:hover': {
                      backgroundColor: isSelected ? '#e0b076' : '#2a2a2a',
                    },
                  }}
                />
              );
            })}
          </Box>
        </Box>

        {/* Prix actuel */}
        {currentPrice !== null && (
          <Card
            sx={{
              backgroundColor: '#1a1a1a',
              border: '1px solid #333',
              borderRadius: '12px',
              mb: 3,
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon sx={{ color: '#4caf50' }} />
                <Typography
                  variant="body2"
                  sx={{ color: 'rgba(255,255,255,0.7)' }}
                >
                  Prix actuel:
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#4caf50',
                    fontWeight: 'bold',
                  }}
                >
                  {currentPrice.toFixed(2)} €
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Formulaire d'ajout */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            {/* Champ de recherche */}
            <Box sx={{ position: 'relative' }}>
              <TextField
                label={t('addCrypto.searchLabel')}
                value={symbol}
                onChange={(e) => handleSearch(e.target.value)}
                disabled={!!selectedSymbol}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#1a1a1a',
                    borderRadius: '8px',
                    '& fieldset': {
                      borderColor: '#333',
                    },
                    '&:hover fieldset': {
                      borderColor: '#f1c086',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#f1c086',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255,255,255,0.7)',
                    '&.Mui-focused': {
                      color: '#f1c086',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    color: '#fff',
                  },
                }}
              />
              
              {showSearchResults && searchResults.length > 0 && (
                <Paper
                  elevation={3}
                  sx={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                  }}
                >
                  <List sx={{ py: 0 }}>
                    {searchResults.map((result) => (
                      <ListItem
                        key={result.symbol}
                        component="div"
                        onClick={() => selectSearchResult(result)}
                        sx={{
                          cursor: 'pointer',
                          borderBottom: '1px solid #333',
                          '&:last-child': {
                            borderBottom: 'none',
                          },
                          '&:hover': {
                            backgroundColor: '#2a2a2a',
                          },
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            src={result.iconUrl}
                            sx={{ width: 32, height: 32 }}
                          >
                            {result.symbol[0]}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={result.symbol}
                          secondary={result.name}
                          primaryTypographyProps={{
                            sx: { color: '#fff' },
                          }}
                          secondaryTypographyProps={{
                            sx: { color: 'rgba(255,255,255,0.7)' },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}
            </Box>

            {/* Quantité */}
            <TextField
              label={t('addCrypto.quantity')}
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              inputProps={{ step: 'any', min: '0' }}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#1a1a1a',
                  borderRadius: '8px',
                  '& fieldset': {
                    borderColor: '#333',
                  },
                  '&:hover fieldset': {
                    borderColor: '#f1c086',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#f1c086',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255,255,255,0.7)',
                  '&.Mui-focused': {
                    color: '#f1c086',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  color: '#fff',
                },
              }}
            />

            {/* Prix d'achat */}
            <TextField
              label={t('addCrypto.buyPrice')}
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              inputProps={{ step: 'any', min: '0' }}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      €
                    </Typography>
                    {isPriceLoading && (
                      <CircularProgress size={20} sx={{ ml: 1, color: '#f1c086' }} />
                    )}
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#1a1a1a',
                  borderRadius: '8px',
                  '& fieldset': {
                    borderColor: '#333',
                  },
                  '&:hover fieldset': {
                    borderColor: '#f1c086',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#f1c086',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255,255,255,0.7)',
                  '&.Mui-focused': {
                    color: '#f1c086',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  color: '#fff',
                },
              }}
            />

            {/* Bouton d'ajout */}
            <Button
              variant="contained"
              onClick={handleAddAsset}
              disabled={!isFormValid()}
              sx={{
                backgroundColor: '#f1c086',
                color: '#000',
                height: '56px',
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: '#e0b076',
                },
                '&:disabled': {
                  backgroundColor: '#333',
                  color: 'rgba(255,255,255,0.3)',
                },
              }}
            >
              {t('onb.capital.addAsset')}
            </Button>
          </Box>
        </Box>

        {/* Liste des actifs ajoutés */}
        {assets.length > 0 && (
          <List sx={{ width: '100%', maxHeight: '200px', overflowY: 'auto', mb: 3 }}>
            {assets.map((asset, index) => (
              <ListItem key={index} sx={{ backgroundColor: '#1a1a1a', mb: 1, borderRadius: '8px' }}>
                <ListItemText
                  primary={
                    <Typography variant="body1" sx={{ color: '#fff' }}>
                      {asset.symbol.toUpperCase()} - {asset.amount} @ {asset.purchasePrice} EUR
                    </Typography>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteAsset(index)}>
                    <DeleteIcon sx={{ color: '#ff6b6b' }} />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}

        {/* Bouton de finalisation */}
        <Button
          fullWidth
          variant="contained"
          onClick={handleNext}
          disabled={loading}
          sx={{
            height: '56px',
            borderRadius: '8px',
            backgroundColor: '#f1c086',
            '&:hover': {
              backgroundColor: '#e0b076',
            },
            color: '#000',
            fontSize: '1rem',
            fontWeight: 'bold',
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : t('onb.capital.finishContinue')}
        </Button>
      </Box>
    </Box>
  );
};

export default WelcomeCapitalScreen;